import { Component, EventEmitter, Output, inject } from '@angular/core';
import { CAREER_AREAS, CAREER_FILTER_DEFAULT_OPTIONS } from '../../../utils/constants';
import { MatSliderModule } from '@angular/material/slider';
import { MatButtonModule } from '@angular/material/button';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FilterFormDataSchema } from '../../../schemas/career-matches';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CareerSearchStore } from '@app/services/signals/career-search-service';
import { Store } from '@ngrx/store';
import { MatTooltipModule } from '@angular/material/tooltip';
import { analytics } from '@app/events/segment/analytics';

@Component({
  selector: 'app-result-filter',
  imports: [
    MatSliderModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    CommonModule,
    MatIconModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
  ],
  templateUrl: './result-filter.component.html',
  styleUrl: './result-filter.component.css'
})
export class ResultFilterComponent {
  @Output() sortEmitter: EventEmitter<string> = new EventEmitter<string>();
  @Output() filterFormEmitter: EventEmitter<
    FilterFormDataSchema
  > = new EventEmitter<FilterFormDataSchema>();
  @Output() searchEmitter = new EventEmitter<string>();
  formbuilder = inject(FormBuilder);
  sortByOptions: string[] = ['Salary'];
  careerAreas = CAREER_AREAS;
  showFilterOptions: boolean = false;
  selectedArea: string[] = CAREER_FILTER_DEFAULT_OPTIONS.area;
  quality = this.formbuilder.group(CAREER_FILTER_DEFAULT_OPTIONS.quality);
  education = this.formbuilder.group(CAREER_FILTER_DEFAULT_OPTIONS.education);
  salary = this.formbuilder.group(CAREER_FILTER_DEFAULT_OPTIONS.salary);
  isExpanded = false;
  searchText = '';
  public readonly matchRankTooltip = `
    Your match quality score shows how highly this career ranks compared to
    other career options — based on your interests, skills, experiences and
    real-world career data.
  `

  constructor(
    private searchStore: CareerSearchStore,
    private store: Store<{
      careerFilter: FilterFormDataSchema
    }>,
  ) {
    this.searchText = this.searchStore.searchText();
    if (this.searchText) {
      this.isExpanded = true;
    }
    this.store.select('careerFilter').subscribe(
      value => {
        this.selectedArea = value.area;
        this.quality.setValue(value.quality);
        this.education.setValue(value.education);
        this.salary.setValue(value.salary);
      }
    );
  }

  sortResults(event: any) {
    const sortValue = event.target.value;
    const sortLabel = sortValue || 'Highest match';
    analytics.track('Filter Sort', { section: 'explore', sort: sortLabel });
    this.sortEmitter.emit(sortValue);
  }

  toggleFilterOptions() {
    this.showFilterOptions = !this.showFilterOptions;
  }

  selectArea(area: string) {
    if (area == 'all') {
      this.selectedArea = [];
    } else {
      const isSelected = this.selectedArea.includes(area);
      const action = isSelected ? 'deselect' : 'select';
      
      if (isSelected) {
        this.selectedArea = this.selectedArea.filter(item => item !== area);
      } else {
        const data = [...this.selectedArea];
        data.push(area);
        this.selectedArea = data;
      }
      
      this.trackFilterSelection('career_area', area, action);
    }
  }

  applyFilters() {
    this.filterFormEmitter.emit({
      area: this.selectedArea,
      quality: {
        h1_plus: this.quality.value.h1_plus || false,
        h1: this.quality.value.h1 || false,
        med: this.quality.value.med || false,
        low: this.quality.value.low || false,
      },
      education: {
        high_school: this.education.value.high_school || false,
        associate_degree: this.education.value.associate_degree || false,
        bachelor_degree: this.education.value.bachelor_degree || false,
        graduate: this.education.value.graduate || false,
      },
      salary: {
        min_value: this.salary.value.min_value || 30,
        max_value: this.salary.value.max_value || 239,
      }
    });
    this.toggleFilterOptions();
  }

  clearFilters() {
    this.selectedArea = [];
    this.quality.reset(CAREER_FILTER_DEFAULT_OPTIONS.quality);
    this.education.reset(CAREER_FILTER_DEFAULT_OPTIONS.education);
    this.salary.reset(CAREER_FILTER_DEFAULT_OPTIONS.salary);
  }

  toggleSearch() {
    this.isExpanded = true;
    setTimeout(() => {
      const input = document.querySelector('input');
      input?.focus();
    }, 100);
  }

  collapseSearch() {
    if (!this.searchText) {
      this.isExpanded = false;
      this.searchStore.reset();
    }
  }

  onSearchCareer() {
    analytics.track('Filter Search', { section: 'explore', search: this.searchText });
    this.searchStore.setSearchText(this.searchText);
    this.searchEmitter.emit(this.searchText);
  }

  private trackFilterSelection(type: string, level: string, action: 'select' | 'deselect') {
    const allFilters = this.getAllCurrentFilters();
    analytics.track('Filter Select', {
      section: 'explore',
      filters: allFilters,
      selected_filter: { type, level },
      action
    });
  }

  onQualityChange(level: string, checked: boolean) {
    const action = checked ? 'select' : 'deselect';
    this.trackFilterSelection('match_rank', level, action);
  }

  onEducationChange(level: string, checked: boolean) {
    const action = checked ? 'select' : 'deselect';
    this.trackFilterSelection('education', level, action);
  }

  onSalaryChange() {
    const minValue = this.salary.value.min_value || 30;
    const maxValue = this.salary.value.max_value || 239;
    const level = `${minValue}-${maxValue}`;
    this.trackFilterSelection('salary', level, 'select');
  }

  private getAllCurrentFilters() {
    const filters: Array<{type: string, level: string}> = [];
    
    // Career areas
    this.selectedArea.forEach(area => {
      filters.push({ type: 'career_area', level: area });
    });
    
    // Match rank (quality)
    Object.entries(this.quality.value).forEach(([key, value]) => {
      if (value) {
        filters.push({ type: 'match_rank', level: key });
      }
    });
    
    // Education
    Object.entries(this.education.value).forEach(([key, value]) => {
      if (value) {
        filters.push({ type: 'education', level: key });
      }
    });
    
    // Salary range (if not default)
    const minValue = this.salary.value.min_value || 30;
    const maxValue = this.salary.value.max_value || 239;
    if (minValue !== 30 || maxValue !== 239) {
      filters.push({ type: 'salary', level: `${minValue}-${maxValue}` });
    }
    
    return filters;
  }
}
